import React, { useState, useRef } from "react";
import { ArrowLeft, Check, Sparkles, UploadCloud, AlertCircle } from "lucide-react";
import { Spinner, SUGGESTED_FIELDS } from "../constants";
import {
  STUDIO_DOC_TYPES, fieldTypeMeta, parseImportJSON,
} from "./studioConst";
import { SX, FRAME, TX } from "./studioTokens";
import { EngineCardGrid } from "../OcrEngineModal";

const STEPS = [
  { id: 1, label: "Basics" },
  { id: 2, label: "Engine" },
  { id: 3, label: "Fields" },
];

const Label = ({ children }) => <div className={`${TX.overline} mb-2`}>{children}</div>;

const FieldRow = ({ field, checked, onToggle }) => {
  const m = fieldTypeMeta(field.data_type);
  return (
    <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-[#ECE9EE] last:border-b-0 cursor-pointer hover:bg-[#F5F5F5]" onClick={onToggle}>
      <input type="checkbox" checked={checked} onChange={onToggle} onClick={(e) => e.stopPropagation()} className="w-4 h-4 accent-[#A04A97]" />
      <span className={`w-[18px] h-[18px] rounded flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${m.badge}`}>{m.glyph}</span>
      <span className="font-mono text-[12.5px] font-semibold text-[#1A1A1A] flex-shrink-0">{field.field_key}</span>
      <span className={`px-2 py-px rounded-full text-[10px] font-semibold flex-shrink-0 ${m.badge}`}>{field.data_type}</span>
      <span className="text-[11.5px] text-[#8A8A90] truncate flex-1">{field.instruction}</span>
    </div>
  );
};

const CreateTemplatePanel = ({ onClose, onCreate, busy }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [docType, setDocType] = useState("invoice");
  const [engine, setEngine] = useState("standard_layout");
  const [mode, setMode] = useState("suggested");           // suggested | import
  const [suggestedUnchecked, setSuggestedUnchecked] = useState(new Set());
  const [importText, setImportText] = useState("");
  const [importUnchecked, setImportUnchecked] = useState(new Set());
  const fileRef = useRef(null);

  const suggested = (SUGGESTED_FIELDS[docType] || []).map((f) => ({
    field_key: f.field_key, data_type: f.data_type, instruction: f.instruction,
  }));
  const parsed = parseImportJSON(importText);

  const chosenFields = mode === "suggested"
    ? suggested.filter((f) => !suggestedUnchecked.has(f.field_key))
    : (parsed.valid ? parsed.fields.filter((f) => !importUnchecked.has(f.field_key)) : []);

  const toggle = (setFn) => (key) => setFn((prev) => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  const toggleSuggested = toggle(setSuggestedUnchecked);
  const toggleImport = toggle(setImportUnchecked);

  const readFile = (file) => { if (!file) return; const r = new FileReader(); r.onload = (e) => setImportText(String(e.target.result || "")); r.readAsText(file); };

  const canBasics = name.trim().length > 0;
  const create = () => onCreate({ name: name.trim(), document_type: docType, engine, fields: chosenFields });

  const tabBtn = (on) =>
    `flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[13px] font-semibold transition-colors ${
      on ? "bg-white text-[#1A1A1A] shadow-[0_1px_4px_rgba(0,0,0,0.08)]" : "text-[#52525B]"
    }`;

  return (
    <div className={`flex flex-col flex-1 min-h-0 font-outfit ${FRAME.canvas}`}>
      {/* Top bar */}
      <div className={`${SX.topbar} gap-3.5`}>
        <button className={`${SX.btn} ${SX.btnGhost}`} onClick={onClose}>
          <ArrowLeft size={15} /> Back
        </button>
        <div className={`w-px h-6 ${SX.divider}`} />
        <div className={TX.screenName}>Create Template Manually</div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className={FRAME.templateView}>
          {/* Stepper */}
          <div className="flex items-center gap-2 mb-5">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <div className="flex items-center gap-2">
                  <span className={`w-[22px] h-[22px] rounded-full text-[11.5px] font-semibold flex items-center justify-center ${
                    step > s.id ? "bg-[#2F7D44] text-white" : step === s.id ? "bg-[#A04A97] text-white" : "bg-white text-[#8A8A90] border-[1.5px] border-[#ECE9EE]"
                  }`}>
                    {step > s.id ? <Check size={11} /> : s.id}
                  </span>
                  <span className={`text-[13px] ${step === s.id ? "font-semibold text-[#A04A97]" : step > s.id ? "font-medium text-[#1A1A1A]" : "font-medium text-[#8A8A90]"}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${SX.divider}`} />}
              </React.Fragment>
            ))}
          </div>

          <div className={`${SX.card} p-7`}>
            {/* Step 1 — basics */}
            {step === 1 && (
              <div>
                <Label>Template Name</Label>
                <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. TJS Group Invoice" className={`${SX.input} mb-5`} />
                <Label>Document Type</Label>
                <select value={docType} onChange={(e) => setDocType(e.target.value)} className={`${SX.input} cursor-pointer`}>
                  {STUDIO_DOC_TYPES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            )}

            {/* Step 2 — engine picker */}
            {step === 2 && (
              <div>
                <div className={`${TX.helper} mb-4 leading-[1.5]`}>Click a card to select the extraction engine for <b className="text-[#1A1A1A] font-semibold">{name || "this template"}</b>. Sample documents show what each engine handles best.</div>
                <EngineCardGrid selected={engine} onChange={setEngine} />
              </div>
            )}

            {/* Step 3 — fields */}
            {step === 3 && (
              <div>
                <div className="flex gap-1.5 mb-4 bg-[#F5F5F5] rounded-[10px] p-1">
                  <button className={tabBtn(mode === "suggested")} onClick={() => setMode("suggested")}>
                    <Sparkles size={13} className={mode === "suggested" ? "text-[#A04A97]" : "text-[#52525B]"} /> Suggested Fields
                  </button>
                  <button className={tabBtn(mode === "import")} onClick={() => setMode("import")}>
                    <UploadCloud size={13} className={mode === "import" ? "text-[#A04A97]" : "text-[#52525B]"} /> Import from JSON
                  </button>
                </div>

                {mode === "suggested" ? (
                  suggested.length === 0 ? (
                    <div className="text-center py-8 text-[#8A8A90] text-[13px] leading-[1.6]">
                      No suggested fields for <b>Custom</b>. Create the template and add fields in the workspace.
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2.5">
                        <div className={TX.helper}>Uncheck any you don't need.</div>
                        <button className="text-[12.5px] font-semibold text-[#A04A97] hover:text-[#893F82]" onClick={() => setSuggestedUnchecked(chosenFields.length ? new Set(suggested.map((f) => f.field_key)) : new Set())}>
                          {chosenFields.length ? "Deselect all" : "Select all"}
                        </button>
                      </div>
                      <div className="border border-[#ECE9EE] rounded-[12px] overflow-hidden">
                        {suggested.map((f) => <FieldRow key={f.field_key} field={f} checked={!suggestedUnchecked.has(f.field_key)} onToggle={() => toggleSuggested(f.field_key)} />)}
                      </div>
                    </>
                  )
                ) : (
                  <>
                    <div className={`${TX.helper} mb-2.5`}>Paste a template JSON, or drop / browse a <code className="text-[11.5px] bg-[#F5F5F5] px-1 rounded">.json</code> file.</div>
                    <div className="relative" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); readFile(e.dataTransfer.files?.[0]); }}>
                      <textarea
                        value={importText} onChange={(e) => setImportText(e.target.value)} rows={8}
                        placeholder={'{\n  "fields": [\n    { "field_key": "invoice_number", "data_type": "text", "instruction": "Extract the invoice number." }\n  ]\n}'}
                        className={`w-full px-4 py-3.5 border-[1.5px] rounded-[12px] text-[11.5px] leading-[1.6] font-mono text-[#E9D2E6] bg-[#1A1A1A] resize-y outline-none ${parsed.error ? "border-[#C77A1C]" : "border-[#ECE9EE] focus:border-[#A04A97]"}`}
                      />
                      <input ref={fileRef} type="file" accept=".json,application/json" hidden onChange={(e) => readFile(e.target.files?.[0])} />
                      <button className="absolute top-2.5 right-2.5 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/25 bg-white/10 text-white text-[11px] font-semibold" onClick={() => fileRef.current?.click()}>
                        <UploadCloud size={12} /> Browse
                      </button>
                    </div>
                    {parsed.error && (
                      <div className="flex items-start gap-1.5 mt-2.5 text-[12px] text-[#B33A1E] leading-[1.5]">
                        <AlertCircle size={13} className="mt-px flex-shrink-0" /> {parsed.error}
                      </div>
                    )}
                    {parsed.valid && (
                      <>
                        <div className="flex items-center gap-2 mt-3 mb-2.5 text-[12px] text-[#2F7D44] font-semibold">
                          <Check size={13} /> {parsed.stats.count} fields · {parsed.stats.tables} table{parsed.stats.tables === 1 ? "" : "s"} · no duplicates
                        </div>
                        <div className="border border-[#ECE9EE] rounded-[12px] overflow-hidden max-h-[260px] overflow-y-auto">
                          {parsed.fields.map((f) => <FieldRow key={f.field_key} field={f} checked={!importUnchecked.has(f.field_key)} onToggle={() => toggleImport(f.field_key)} />)}
                        </div>
                      </>
                    )}
                  </>
                )}
                <div className="text-[12px] text-[#8A8A90] mt-3">{chosenFields.length} field{chosenFields.length === 1 ? "" : "s"} selected — you can add, edit or remove fields after creating.</div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-5">
            {step > 1 ? <button className="text-[13px] font-semibold text-[#A04A97] hover:text-[#893F82]" onClick={() => setStep((s) => s - 1)}>← Back</button> : <span />}
            {step === 1 && (
              <button className={`${SX.btn} ${SX.btnPrimary}`} disabled={!canBasics} onClick={() => setStep(2)}>Next: Choose Engine →</button>
            )}
            {step === 2 && (
              <button className={`${SX.btn} ${SX.btnPrimary}`} onClick={() => setStep(3)}>Continue →</button>
            )}
            {step === 3 && (
              <button className={`${SX.btn} ${SX.btnPrimary}`} disabled={busy} onClick={create}>
                {busy ? <><Spinner size={14} /> Creating…</> : <><Check size={14} /> Create Template →</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplatePanel;
